import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FileType, Info } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { API_ENDPOINTS } from "@/config/api";

interface UploadFormProps {
  onFileSelect: (file: File) => void;
  isUploading: boolean;
  isProcessing: boolean;
  onUploadComplete?: () => void;
}

export function UploadForm({ onFileSelect, isUploading, isProcessing, onUploadComplete }: UploadFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();
  const [isUploadingState, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form state when isUploading changes to false
  useEffect(() => {
    if (!isUploading) {
      setIsUploading(false);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isUploading]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.name.endsWith('.docx')) {
        toast({
          title: "Invalid file type",
          description: "Please upload a DOCX file",
          variant: "destructive",
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setFile(file);
      console.log("File selected:", file.name);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!file.name.endsWith('.docx')) {
        toast({
          title: "Invalid file type",
          description: "Please upload a DOCX file",
          variant: "destructive",
        });
        return;
      }
      setFile(file);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      // Get authenticated user
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("You must be logged in to upload templates");
      }
      
      const userId = session.user.id;
      
      // Generate a unique filename for storage with user folder structure
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const sanitizedFilename = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const storagePath = `${userId}/${sanitizedFilename}_${timestamp}.${fileExtension}`;
      
      // Upload the file to Supabase Storage
      console.log("Uploading file to storage...");
      const { data: uploadData, error: uploadError } = await supabase
        .storage
        .from('templates')
        .upload(storagePath, file);
      
      if (uploadError) {
        throw uploadError;
      }
      
      // Create template record with pending status
      console.log("Creating template record...");
      const { data: templateData, error: dbError } = await supabase
        .from('templates')
        .insert({
          user_id: userId,
          filename: file.name,
          file_path: storagePath,
          size: file.size,
          content_type: file.type,
          processing_status: 'pending',
          needs_review: true,
          is_approved: false
        })
        .select()
        .single();

      if (dbError) {
        // Cleanup the uploaded file if database insert fails
        await supabase.storage
          .from('templates')
          .remove([storagePath]);
        throw dbError;
      }

      // Trigger document processing
      console.log("Triggering document processing...");
      const response = await fetch(API_ENDPOINTS.PROCESS_TEMPLATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templateId: templateData.id })
      });

      if (!response.ok) {
        throw new Error('Failed to trigger document processing');
      }

      toast({
        title: "Upload successful",
        description: "Your template has been uploaded and is being processed.",
      });

      // Clear the file input
      setFile(null);
      
      // Notify parent component that upload is complete
      onFileSelect(file);
      onUploadComplete?.();
      
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "There was an error uploading your template",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div 
        className={cn(
          "border-2 border-dashed rounded-lg p-8 transition-colors",
          dragActive ? "border-primary bg-primary/5" : "border-gray-300",
        )}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4 space-y-2">
            <Input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
              accept=".docx"
              disabled={isUploadingState || isProcessing}
            />
            <label
              htmlFor="file-upload"
              className={cn(
                "cursor-pointer text-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2",
                (isUploadingState || isProcessing) && "opacity-50 cursor-not-allowed"
              )}
            >
              <FileType className="h-4 w-4" />
              Choose a DOCX file
            </label>
            <p className="text-sm text-gray-500">
              or drop file here
            </p>
            {file ? (
              <div className="mt-2 text-sm font-medium flex items-center gap-2 justify-center">
                <FileType className="h-4 w-4 text-primary" />
                {file.name}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center text-xs text-muted-foreground gap-1 cursor-help">
                <Info className="h-3 w-3" /> 
                <span>Supported formats</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Currently, only DOCX files are supported</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Button
          onClick={handleUpload}
          disabled={!file || isUploadingState || isProcessing}
          className="w-auto"
        >
          {isProcessing ? "Processing..." : isUploadingState ? "Uploading..." : "Upload Template"}
        </Button>
      </div>
    </div>
  );
}
